import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { rideAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import StarRating from '../../components/StarRating';

const VEHICLE_LABELS = { taxi: '🚕 تاكسي', bus: '🚌 حافلة', rickshaw: '🛺 ركشة' };

const REQUEST_STATUS_CFG = {
  open_for_offers: { label: 'بانتظار العروض', color: 'bg-amber-100 text-amber-700' },
  offer_selected:  { label: 'تم اختيار عرض', color: 'bg-blue-100 text-blue-700' },
  cancelled:       { label: 'ملغى', color: 'bg-red-100 text-red-700' },
  expired:         { label: 'منتهي', color: 'bg-gray-100 text-gray-500' },
};

const ORDER_STATUS_CFG = {
  accepted:        { label: 'تم القبول', color: 'bg-blue-100 text-blue-700' },
  driver_en_route: { label: 'السائق في الطريق', color: 'bg-indigo-100 text-indigo-700' },
  arrived:         { label: 'وصل السائق', color: 'bg-purple-100 text-purple-700' },
  in_progress:     { label: 'المشوار جارٍ', color: 'bg-purple-100 text-purple-700' },
  completed:       { label: 'انتهى المشوار', color: 'bg-emerald-100 text-emerald-700' },
  cancelled:       { label: 'ملغى', color: 'bg-red-100 text-red-700' },
};

const RideCard = ({ req, navigate }) => {
  const pendingOffers = (req.offers || []).filter((o) => o.status === 'pending').length;
  const target = req.order ? `/rides/orders/${req.order.id}` : `/rides/requests/${req.id}`;
  const statusCfg = req.order
    ? (ORDER_STATUS_CFG[req.order.status] || { label: req.order.status, color: 'bg-gray-100 text-gray-600' })
    : (REQUEST_STATUS_CFG[req.status] || { label: req.status, color: 'bg-gray-100 text-gray-600' });

  return (
    <button onClick={() => navigate(target)} className="w-full text-right bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:border-blue-200 transition-colors">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusCfg.color}`}>{statusCfg.label}</span>
        <span className="text-xs text-gray-500">{VEHICLE_LABELS[req.vehicleCategory] || req.vehicleCategory}</span>
      </div>
      <p className="font-bold text-gray-800 mb-1">{req.fromLabel} ← {req.toLabel}</p>
      {!req.order && req.status === 'open_for_offers' && (
        <p className="text-xs font-semibold text-blue-700">
          {pendingOffers > 0 ? `🚕 ${pendingOffers} عرض بانتظار مراجعتك` : 'بانتظار عروض السائقين...'}
        </p>
      )}
      {req.order && <p className="text-sm font-black text-emerald-600">{req.order.price} ج.س</p>}
      {req.order?.status === 'completed' && (
        req.order.review ? (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-gray-500">تقييمك:</span>
            <StarRating value={req.order.review.rating} readOnly size="text-sm" />
          </div>
        ) : (
          <p className="text-xs font-semibold text-amber-600 mt-2">⭐ قيّم رحلتك</p>
        )
      )}
    </button>
  );
};

const RideRequestsPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await rideAPI.getMyRequests();
      setRequests(res.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [isLoggedIn]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { if (!isLoggedIn) navigate('/login?from=/rides'); }, [isLoggedIn, navigate]);

  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-black text-[#1E3A8A]">🚕 مشاويري</h1>
          </div>
          <button onClick={() => navigate('/rides/new')} className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all">
            + طلب مشوار
          </button>
        </div>

        {!isLoggedIn ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <p className="text-gray-600 font-semibold mb-4">سجّل الدخول لعرض مشاويرك</p>
            <button onClick={() => navigate('/login?from=/rides')} className="bg-[#1E40AF] text-white font-bold px-6 py-2.5 rounded-xl">تسجيل الدخول</button>
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-gray-400">جاري التحميل...</div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <div className="text-5xl mb-3 opacity-60">🚕</div>
            <p className="text-gray-600 font-semibold">لا توجد مشاوير بعد</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => <RideCard key={r.id} req={r} navigate={navigate} />)}
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default RideRequestsPage;
